import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MDRBadge } from "@/components/MDRBadge";
import {
  accreditedStructuresData, specialistAssignmentsData, bookingsData, getStructureById
} from "@/data/platformData";
import { doctorsData, examReportsData, getPatientById } from "@/data/mockData";
import { Hospital, Stethoscope, FileSignature, Receipt, Building2, Clock } from "lucide-react";

export const StructureHomePage = () => {
  // Mock: this manager handles str1
  const structure = getStructureById("str1");
  const specialists = doctorsData.filter(d => structure.specialistIds.includes(d.id));
  const incomingBookings = bookingsData.filter(b => b.structureId === "str1");
  const reports = examReportsData; // mock all reports
  const pendingCount = reports.filter(r => r.status === "pending").length;
  const signedCount = reports.filter(r => r.status === "signed").length;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Hospital className="h-4 w-4 text-cyan-600" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Responsabile Struttura Accreditata</span>
            <MDRBadge variant="admin" size="sm" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{structure.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">{structure.address}</p>
        </div>
      </div>

      {/* KPI */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Medici Specialisti", value: specialists.length, icon: Stethoscope, accent: "from-cyan-500/10 to-cyan-500/5 text-cyan-700" },
          { label: "Esami in arrivo", value: incomingBookings.length, icon: Clock, accent: "from-amber-500/10 to-amber-500/5 text-amber-700" },
          { label: "Da refertare", value: pendingCount, icon: FileSignature, accent: "from-rose-500/10 to-rose-500/5 text-rose-700" },
          { label: "Refertati", value: signedCount, icon: Receipt, accent: "from-emerald-500/10 to-emerald-500/5 text-emerald-700" }
        ].map(k => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className={`bg-gradient-to-br ${k.accent} border-2`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-white/60 dark:bg-black/20 flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold leading-none">{k.value}</p>
                  <p className="text-xs mt-1 opacity-80">{k.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Specialists */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Stethoscope className="h-4 w-4 text-cyan-600" /> Medici Specialisti
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {specialists.map(d => {
              const assignment = specialistAssignmentsData.find(a => a.specialistId === d.id && a.structureId === "str1");
              return (
                <div key={d.id} className="p-3 rounded-lg border bg-card">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-sm">{d.name}</p>
                      <p className="text-xs text-muted-foreground">{d.specialization}</p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">{d.licenseNumber}</Badge>
                  </div>
                  {assignment && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {assignment.services.map(s => (
                        <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Pharmacies served */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-indigo-600" /> Farmacie convenzionate
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {structure.associatedPharmacies.map(phId => {
              const pharm = bookingsData.find(b => b.pharmacyId === phId);
              return (
                <div key={phId} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <span className="text-sm font-medium">{phId.toUpperCase()}</span>
                  <Badge variant="outline">Attiva</Badge>
                </div>
              );
            })}
            <p className="text-xs text-muted-foreground pt-2">Servizi gestiti: {structure.handledServices.join(", ")}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-dashed border-2 bg-muted/30">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <Receipt className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="font-semibold text-sm">Roadmap modulo Struttura Accreditata</p>
              <p className="text-xs text-muted-foreground">
                Assegnazione prestazioni → specialista, monitoraggio refertazioni, rendicontazione fatturato per farmacia.
              </p>
            </div>
          </div>
          <Badge variant="outline">Fase 4</Badge>
        </CardContent>
      </Card>
    </div>
  );
};

export default StructureHomePage;
